import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onValue, ref } from 'firebase/database';
import { COURSE_PERIODS, COURSES_DATA } from '../data/coursesData';
import { database } from '../firebase';
import { normalizeCourse } from '../utils/courseStructure';
import { Sparkles, Calendar, BookOpen, ChevronLeft, ChevronRight, ArrowRight, ShieldAlert, Award, CreditCard, Play } from 'lucide-react';
import '../styles/landing.css';

export default function CursosCarousel() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activePeriodId, setActivePeriodId] = useState('p1');
  const [rtdbCourses, setRtdbCourses] = useState(null); // null = aún no cargado

  // Carga los cursos desde RTDB (donde el admin guarda contenido). Fallback a datos estáticos.
  useEffect(() => {
    const unsubscribe = onValue(ref(database, 'vortex_web_courses'), (snap) => {
      if (snap.exists()) {
        const val = snap.val();
        const list = Object.keys(val).map((id) => normalizeCourse({ id, ...val[id] }));
        setRtdbCourses(list.length > 0 ? list : COURSES_DATA);
      } else {
        setRtdbCourses(COURSES_DATA);
      }
    }, () => setRtdbCourses(COURSES_DATA));
    return () => unsubscribe();
  }, []);

  // Filtrar cursos por periodo o mostrar destacados
  const featuredCourses = rtdbCourses || COURSES_DATA;

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % featuredCourses.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + featuredCourses.length) % featuredCourses.length);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 6000);
    return () => clearInterval(timer);
  }, [currentIndex]);

  const currentCourse = featuredCourses[currentIndex] || featuredCourses[0];

  return (
    <section id="cursos-vortex-teaser" style={{ padding: 'var(--spacing-lg) 0', background: 'var(--bg-secondary)', position: 'relative' }}>
      <div className="container">
        
        {/* Header de la sección */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-lg)' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 26px',
            borderRadius: '50px',
            background: 'rgba(99, 102, 241, 0.15)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            color: '#a5b4fc',
            fontSize: '2.5rem',
            fontWeight: '700',
            marginBottom: '0.75rem'
          }}>
            <Sparkles style={{ width: 26, height: 26 }} />
            <span>Plataforma de Capacitación y Cursos Online</span>
          </div>

          <h2 style={{ fontSize: '2.25rem', fontWeight: '800', color: 'var(--text-primary)', marginBottom: '1rem' }}>
            Nuevos <span style={{ background: 'linear-gradient(135deg, #818cf8 0%, #38bdf8 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Cursos Vortex</span> y Periodos de Estudio
          </h2>

          <p style={{ color: 'var(--text-secondary)', maxWidth: '650px', margin: '0 auto', fontSize: '1rem' }}>
            Preparación teórica continua con temarios estructurados, evaluaciones interactivas y material descargable en PDF divididos en 3 periodos anuales.
          </p>
        </div>

        {/* Tarjetas de los 3 Periodos de Estudio */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginBottom: '2.5rem' }}>
          {COURSE_PERIODS.map((period) => {
            const isSelected = activePeriodId === period.id;
            return (
              <div
                key={period.id}
                onClick={() => setActivePeriodId(period.id)}
                style={{
                  padding: '1.25rem',
                  borderRadius: '16px',
                  background: isSelected ? 'rgba(99, 102, 241, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                  border: isSelected ? '1px solid rgba(99, 102, 241, 0.5)' : '1px solid rgba(255, 255, 255, 0.08)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#818cf8' }}>{period.shortName}</span>
                    <span style={{
                      fontSize: '0.7rem',
                      fontWeight: '700',
                      padding: '2px 8px',
                      borderRadius: '20px',
                      background: period.active ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                      color: period.active ? '#34d399' : '#94a3b8'
                    }}>
                      {period.badge}
                    </span>
                  </div>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', color: 'white', marginBottom: '0.25rem' }}>
                    {period.name}
                  </h3>
                  <p style={{ fontSize: '0.8rem', color: '#94a3b8', lineHeight: '1.4' }}>
                    {period.description}
                  </p>
                </div>
                <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: '0.75rem', color: '#cbd5e1' }}>
                  📅 Fechas: {period.months}
                </div>
              </div>
            );
          })}
        </div>

        {/* Nota informativa Exámenes SEMAR (Junio, Julio, Agosto) */}
        <div style={{
          padding: '1rem 1.25rem',
          borderRadius: '16px',
          background: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '2.5rem'
        }}>
          <div style={{
            padding: '10px',
            borderRadius: '12px',
            background: 'rgba(245, 158, 11, 0.2)',
            color: '#fbbf24',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <ShieldAlert style={{ width: 22, height: 22 }} />
          </div>
          <div>
            <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#fef3c7' }}>
              Convocatoria de Admisión y Aplicación EXANI-II 2026
            </h4>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: '#fde68a', lineHeight: '1.4' }}>
              Prepárate con tiempo para la fase académica del Ceneval. Los 4 cursos troncales, simuladores de 168 reactivos y módulos disciplinares están completamente integrados en la plataforma <strong>Vortex EXANI-II</strong>.
            </p>
          </div>
        </div>

        {/* Carrusel de Cursos Destacados */}
        {currentCourse && (
          <div style={{
            background: 'rgba(15, 23, 42, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
            position: 'relative'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
              
              {/* Columna Izquierda: Imagen */}
              <div style={{ position: 'relative', minHeight: '280px', background: '#090d16' }}>
                <img
                  src={currentCourse.thumbnail}
                  alt={currentCourse.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to right, transparent 60%, rgba(15, 23, 42, 0.95))'
                }}></div>
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  left: '1rem',
                  display: 'flex',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    background: 'rgba(15, 23, 42, 0.85)',
                    border: '1px solid rgba(99, 102, 241, 0.4)',
                    color: '#a5b4fc',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    backdropFilter: 'blur(4px)'
                  }}>
                    {currentCourse.category}
                  </span>
                </div>
              </div>

              {/* Columna Derecha: Detalles del Curso */}
              <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span style={{ color: '#fbbf24', fontWeight: '800', fontSize: '1.1rem' }}>
                      $300 MXN <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '400' }}>/ Periodo Activo (Incluye los 4 Cursos)</span>
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                      📚 {currentCourse.total_lessons} Lecciones · {currentCourse.duration_minutes} min
                    </span>
                  </div>

                  <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white', marginBottom: '0.75rem', lineHeight: '1.3' }}>
                    {currentCourse.title}
                  </h3>

                  <p style={{ fontSize: '0.875rem', color: '#cbd5e1', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                    {currentCourse.description}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <button
                    onClick={() => navigate('/cursos')}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                      border: 'none',
                      color: 'white',
                      fontWeight: '700',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      boxShadow: '0 8px 20px rgba(99, 102, 241, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span>Explorar Catálogo de Cursos</span>
                    <ArrowRight style={{ width: 16, height: 16 }} />
                  </button>

                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={prevSlide}
                      style={{
                        padding: '10px',
                        borderRadius: '12px',
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <ChevronLeft style={{ width: 18, height: 18 }} />
                    </button>
                    <button
                      onClick={nextSlide}
                      style={{
                        padding: '10px',
                        borderRadius: '12px',
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <ChevronRight style={{ width: 18, height: 18 }} />
                    </button>
                  </div>
                </div>

              </div>

            </div>
          </div>
        )}

      </div>
    </section>
  );
}
